/**
 * 10.9BY — SKILLS + TREINO. Sistema À PARTE da Maestria por arma (que continua): 7 perícias
 * (6 de arma + Defesa) que sobem por TREINO, online (lutando) e offline (Boneco de Treino no hub,
 * por tempo real, pago em 🪙 por hora e acelerável com 💎). Quanto mais skill, mais forte.
 *
 * Só ESTADO + FÓRMULAS (puro). O Game orquestra (XP por golpe, banner, recalcPlayer); a tela
 * TrainingScreen só desenha. Estado vive em GameProgress.skills / GameProgress.training.
 */
import { GameConfig } from "../config/GameConfig";
import type { WeaponType } from "../items/EquipmentTypes";

export type SkillId = "sword" | "axe" | "mace" | "ranged" | "magic" | "summon" | "defense";
export const SKILL_IDS: readonly SkillId[] = ["sword", "axe", "mace", "ranged", "magic", "summon", "defense"];
export const SKILL_ICON: Record<SkillId, string> = { sword: "⚔️", axe: "🪓", mace: "🔨", ranged: "🏹", magic: "🔮", summon: "💀", defense: "🛡️" };

export interface SkillState { level: number; xp: number }
export type SkillsState = Record<SkillId, SkillState>;

/** Treino OFFLINE em andamento (1 por vez). Tempos em epoch ms. */
export interface TrainingState {
  skill: SkillId;
  startedAt: number; // avança a cada coleta parcial (o que já foi creditado sai daqui)
  endsAt: number;
  ratePerMin: number; // XP por minuto (fixado ao iniciar, pelo nível da skill na hora)
}

export function emptySkills(): SkillsState {
  const s = {} as SkillsState;
  for (const id of SKILL_IDS) s[id] = { level: 1, xp: 0 };
  return s;
}

/** Sanitiza um snapshot (save antigo/campo faltando → nível 1). */
export function loadSkills(raw: unknown): SkillsState {
  const out = emptySkills();
  if (!raw || typeof raw !== "object") return out;
  const cfg = GameConfig.skills;
  for (const id of SKILL_IDS) {
    const v = (raw as Record<string, Partial<SkillState> | undefined>)[id];
    if (!v || typeof v !== "object") continue;
    const level = Math.max(1, Math.min(cfg.maxLevel, Math.floor(Number(v.level ?? 1) || 1)));
    const xp = Math.max(0, Math.floor(Number(v.xp ?? 0) || 0));
    out[id] = { level, xp: level >= cfg.maxLevel ? 0 : Math.min(xp, xpFor(level) - 1) };
  }
  return out;
}

export function loadTraining(raw: unknown): TrainingState | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Partial<TrainingState>;
  if (!SKILL_IDS.includes(t.skill as SkillId)) return null;
  const startedAt = Math.floor(Number(t.startedAt ?? 0) || 0), endsAt = Math.floor(Number(t.endsAt ?? 0) || 0);
  if (startedAt <= 0 || endsAt <= startedAt) return null;
  return { skill: t.skill as SkillId, startedAt, endsAt, ratePerMin: Math.max(1, Math.floor(Number(t.ratePerMin ?? 1) || 1)) };
}

/** XP necessário pra sair do nível `l`: 40 × l^1.5 (Nv1=40 · Nv10=1265 · Nv50=14142 · Nv99=39400). */
export function xpFor(level: number): number {
  return Math.floor(GameConfig.skills.xpBase * Math.pow(level, GameConfig.skills.xpExp));
}

/** Soma XP e aplica level-ups em cadeia; devolve quantos níveis subiu. */
export function addSkillXp(state: SkillsState, id: SkillId, amount: number): number {
  const s = state[id];
  const max = GameConfig.skills.maxLevel;
  if (s.level >= max) { s.xp = 0; return 0; }
  s.xp += Math.max(0, Math.floor(amount));
  let ups = 0;
  while (s.level < max && s.xp >= xpFor(s.level)) {
    s.xp -= xpFor(s.level);
    s.level++;
    ups++;
  }
  if (s.level >= max) s.xp = 0;
  return ups;
}

/** Skill da arma: bow→ranged, staff→magic; necromante usa Invocação com qualquer arma. */
export function skillForWeapon(weapon: WeaponType | null, classId: string): SkillId | null {
  if (classId === "necromancer") return "summon";
  if (!weapon) return null;
  switch (weapon) {
    case "sword": return "sword";
    case "axe": return "axe";
    case "mace": return "mace";
    case "bow": return "ranged";
    case "staff": return "magic";
    case "crossbow": return "ranged"; // 10.9L25
    case "tome": return "magic"; // 10.9L25
    default: return null;
  }
}

/** Bônus de skill de ARMA: +0.8% dano/nível (máx +80%) · +2% vel. de ataque a cada 10 níveis. */
export function weaponSkillBonus(level: number): { damagePct: number; attackSpeedPct: number } {
  const b = GameConfig.skills.bonus;
  return {
    damagePct: Math.min(b.damageCap, level * b.damagePerLevel),
    attackSpeedPct: Math.floor(level / b.attackSpeedEvery) * b.attackSpeedStep,
  };
}

/** Bônus de DEFESA: +0.4% de redução de dano/nível (máx 40%), entra como `resilience`. */
export function defenseSkillBonus(level: number): number {
  const b = GameConfig.skills.bonus;
  return Math.min(b.defenseCap, level * b.defensePerLevel);
}

// ---- treino offline ----
/** 🪙 por hora de treino: 20 + nível×4 (Nv20 = 100🪙/h). */
export function trainCostPerHour(level: number): number {
  const t = GameConfig.skills.training;
  return t.costBase + level * t.costPerLevel;
}
/** 💎 equivalentes pra um custo em 🪙 (mín. 1). */
export function trainDiamondCost(coins: number): number {
  return Math.max(1, Math.ceil(coins / GameConfig.skills.training.coinsPerDiamond));
}
/** XP por minuto: 2 + floor(nível/10). */
export function trainRatePerMin(level: number): number {
  const t = GameConfig.skills.training;
  return t.rateBase + Math.floor(level / t.rateEvery);
}
/** XP já rendido (não coletado) — minutos inteiros. */
export function trainPendingXp(t: TrainingState, now = Date.now()): number {
  const mins = Math.floor((Math.min(now, t.endsAt) - t.startedAt) / 60000);
  return Math.max(0, mins) * t.ratePerMin;
}
/** 💎 pra terminar agora: ceil(minutos restantes / 30). */
export function trainFinishCost(t: TrainingState, now = Date.now()): number {
  const minsLeft = Math.ceil(Math.max(0, t.endsAt - now) / 60000);
  return minsLeft <= 0 ? 0 : Math.max(1, Math.ceil(minsLeft / GameConfig.skills.training.finishMinsPerDiamond));
}
